import { LazyLoading } from "@/controls/LazyLoading";
import { AssetsFilter } from "@/controls/assetsFilter/AssetsFilter";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import {
	ShiftOption,
	ShiftSelectorAutocomplete,
} from "@/controls/shift-selector/ShiftSelector";
import { getShiftByParameters } from "@/pages/caster/repositories/ProductionRepository";
import {
	ErrorModal,
	dxtToLocalServerTime,
	getMomentTz,
	useShiftNavigator,
	useUserContext,
} from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	DialogContent,
	DialogTitle,
	Grid,
	Paper,
	Snackbar,
	TextField,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { DatePicker } from "@mui/x-date-pickers";
import { Moment } from "moment-timezone";
import {
	SyntheticEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useLogbookContext } from "../../context/logbookContext";
import { useSearchEntries } from "../../hooks/useSearchEntries";
import { useSearchEntry } from "../../hooks/useSearchEntry";
import { Entry } from "../../models/Entry";
import { ENTRY_INITIAL_VALUES } from "../../models/EntryInitialValue";
import { upsertEntry } from "../../repository/LogbookRepository";
import { ContentInput } from "./content-input";
import { useEntryViewerStyles } from "./styles/useEntryViewerStyles";

const moment = getMomentTz();

export const EntryViewerV2 = () => {
	const classes = useEntryViewerStyles();
	const { EntryId } = useParams<{ EntryId: string }>();
	const navigate = useNavigate();
	const { shiftInfo } = useShiftNavigator();
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const {
		state: {
			EntrySelectedInTable,
			isEditing,
			FilterParameters: { AssetId: filterAssetId },
		},
		actions: { setEntrySelectedInTable, setIsEditing },
	} = useLogbookContext();

	const isNewEntry = EntryId === "new";

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
	const [addSuccess, setAddSuccess] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const [showConfirmationDialog, setShowConfirmationDialog] =
		useState<boolean>(false);
	const [pendingSubmitData, setPendingSubmitData] = useState<Entry | null>(
		null,
	);

	const prevEntryIdRef = useRef<string | undefined>();
	const formInitializedForEntryIdRef = useRef<string | undefined>();
	const initialShiftSetRef = useRef<boolean>(false);

	const currentProductionShiftRef = useRef<ShiftOption | null>(null);
	const manuallySelectedShiftRef = useRef<ShiftOption | null>(null);

	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors, isValid },
		reset,
		watch,
		getValues,
	} = useForm<Entry>({
		defaultValues: ENTRY_INITIAL_VALUES,
	});

	const watchedEntryIdFormField = watch("EntryId");
	const watchedContent = watch("Content");
	const watchedUpdatedBy = watch("UpdatedBy");
	const watchedUpdateTimestamp = watch("UpdateTimestamp");

	const searchEntries = useSearchEntries({
		setIsLoading: setIsLoading,
		setError: setError,
	});

	const searchEntry = useSearchEntry({
		setIsEntryLoading: setIsLoading,
		setEntryError: setError,
		EntryId: EntryId as string,
	});

	const convertToServerTime = useCallback((utcTime: string | Date) => {
		if (utcTime) {
			const serverTimeString = dxtToLocalServerTime(
				utcTime,
				"MM/dd/yy hh:mm a",
			);
			return moment(serverTimeString, "MM/DD/YY hh:mm a").toDate();
		}
		return null;
	}, []);

	const handleCloseSnackbar = (event?: SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setAddSuccess(false);
	};

	const proceedToSubmit = async (data: Entry) => {
		setIsSubmitLoading(true);
		setError(null);
		const {
			EntryId: currentEntryIdVal,
			AssetId,
			Title,
			Content,
			Author,
			CreateTimestamp,
		} = data;

		const effectiveShiftId = data.ShiftId || shiftInfo?.CurrentShiftId;

		if (typeof effectiveShiftId !== "number") {
			setError("Shift ID is missing or invalid.");
			setIsSubmitLoading(false);
			return;
		}

		const res = await upsertEntry(
			currentEntryIdVal,
			effectiveShiftId,
			AssetId,
			Title,
			Content,
			CreateTimestamp as Date,
			Author as string,
		);

		if (res.ok && res.data?.tables?.[0]?.rows?.[0]) {
			setAddSuccess(true);
			searchEntries();
			const savedEntry = res.data.tables[0].rows[0];
			setEntrySelectedInTable(savedEntry);
			if (isNewEntry) {
				formInitializedForEntryIdRef.current = undefined;
				prevEntryIdRef.current = undefined;
				shiftInfo?.CurrentShiftId === savedEntry.ShiftId
					? navigate(`/logbook-entry/`, {
							replace: true,
							relative: "path",
						})
					: window.location.reload();
			} else {
				toggleEdit();
				formInitializedForEntryIdRef.current = String(savedEntry.EntryId);
			}
		} else {
			const errorRes = res as { message?: string };
			setError(errorRes.message || "Failed to save entry.");
		}
		setIsSubmitLoading(false);
	};

	const onSubmit = async (formData: Entry) => {
		setError(null);
		setIsSubmitLoading(true);

		const submissionData = { ...formData };
		if (isNewEntry) {
			submissionData.CreateTimestamp = new Date();
		}

		let shiftForValidation: ShiftOption | null = null;
		const formShiftId = submissionData.ShiftId;

		if (formShiftId) {
			if (manuallySelectedShiftRef.current?.ShiftId === formShiftId) {
				shiftForValidation = manuallySelectedShiftRef.current;
			} else if (currentProductionShiftRef.current?.ShiftId === formShiftId) {
				shiftForValidation = currentProductionShiftRef.current;
			} else {
				shiftForValidation = {
					ShiftId: formShiftId,
					Shift: submissionData.Shift || "",
					Crew: submissionData.Crew || "",
					Start: currentProductionShiftRef.current?.Start || new Date(0),
					End: currentProductionShiftRef.current?.End || new Date(0),
					ProductionDate:
						currentProductionShiftRef.current?.ProductionDate || new Date(0),
					ProductionShift:
						currentProductionShiftRef.current?.ProductionShift || 0,
				};
			}
		}

		if (
			shiftForValidation &&
			shiftForValidation.Start &&
			shiftForValidation.End &&
			moment(shiftForValidation.Start).isValid() &&
			moment(shiftForValidation.End).isValid() &&
			!moment(shiftForValidation.Start).isSame(new Date(0)) &&
			!moment().isBetween(
				moment(shiftForValidation.Start),
				moment(shiftForValidation.End),
				undefined,
				"[]",
			)
		) {
			setPendingSubmitData(submissionData);
			setShowConfirmationDialog(true);
			setIsSubmitLoading(false);
		} else {
			await proceedToSubmit(submissionData);
		}
	};

	const handleConfirmationClose = (value?: string) => {
		setShowConfirmationDialog(false);
		if (value === "OK" && pendingSubmitData) {
			proceedToSubmit(pendingSubmitData);
		}
		setPendingSubmitData(null);
	};

	const toggleEdit = () => {
		setIsEditing(!isEditing);
	};

	const toggleClose = () => {
		setEntrySelectedInTable({} as Entry);
		formInitializedForEntryIdRef.current = undefined;
		prevEntryIdRef.current = undefined;
		navigate(`/logbook-entry`, { replace: true });
	};

	const disabledField =
		(isNewEntry && initialShiftSetRef.current) || (!isNewEntry && !isEditing);

	let confirmationDialogMessage = "";
	if (showConfirmationDialog && pendingSubmitData) {
		const shiftToDisplayForDialog =
			manuallySelectedShiftRef.current?.ShiftId === pendingSubmitData.ShiftId
				? manuallySelectedShiftRef.current
				: currentProductionShiftRef.current?.ShiftId ===
						pendingSubmitData.ShiftId
					? currentProductionShiftRef.current
					: null;

		if (
			shiftToDisplayForDialog &&
			shiftToDisplayForDialog.Start &&
			moment(shiftToDisplayForDialog.Start).isValid() &&
			!moment(shiftToDisplayForDialog.Start).isSame(new Date(0))
		) {
			confirmationDialogMessage = `You are about to ${
				isNewEntry ? "create" : "save"
			} an entry for ${moment(shiftToDisplayForDialog.Start).format(
				"MM/DD/YYYY",
			)} ${shiftToDisplayForDialog.Shift || pendingSubmitData.Shift} - ${
				shiftToDisplayForDialog.Crew || pendingSubmitData.Crew
			}. Are you sure you want to proceed?`;
		} else {
			confirmationDialogMessage = `The selected shift (${
				pendingSubmitData.Shift || "N/A"
			} - ${
				pendingSubmitData.Crew || "N/A"
			}) may be outside the current time or its details are unclear. Are you sure you want to proceed?`;
		}
	}

	//UseEffects
	useEffect(() => {
		if (prevEntryIdRef.current === EntryId && EntryId !== undefined) {
			return;
		}

		setIsLoading(true);
		setError(null);
		manuallySelectedShiftRef.current = null;
		initialShiftSetRef.current = false;

		if (!isNewEntry) {
			formInitializedForEntryIdRef.current = undefined;
		}

		if (isNewEntry) {
			const calculatedAssetId =
				typeof filterAssetId === "number" && filterAssetId !== 0
					? filterAssetId
					: defaultAreaId && defaultAreaId !== 10
						? Number(defaultAreaId)
						: null;

			reset({
				...ENTRY_INITIAL_VALUES,
				AssetId: calculatedAssetId,
				CreateTimestamp: new Date(),
			});
			formInitializedForEntryIdRef.current = "new";
			setIsLoading(false);
		} else if (EntryId) {
			searchEntry().catch((e) => {
				setError(e.message || "Failed to load entry");
				setIsLoading(false);
			});
		} else {
			reset(ENTRY_INITIAL_VALUES);
			formInitializedForEntryIdRef.current = undefined;
			setIsLoading(false);
		}

		prevEntryIdRef.current = EntryId;
	}, [
		EntryId,
		isNewEntry,
		reset,
		defaultAreaId,
		filterAssetId,
		searchEntry,
		setIsLoading,
		setError,
	]);

	useEffect(() => {
		if (!isNewEntry && EntrySelectedInTable && EntrySelectedInTable.EntryId) {
			if (
				formInitializedForEntryIdRef.current ===
					String(EntrySelectedInTable.EntryId) &&
				getValues("EntryId") === EntrySelectedInTable.EntryId
			) {
				return;
			}
			reset({
				EntryId: EntrySelectedInTable.EntryId,
				ShiftId: EntrySelectedInTable.ShiftId,
				AssetId: EntrySelectedInTable.AssetId,
				Title: EntrySelectedInTable.Title,
				Shift: EntrySelectedInTable.Shift,
				Crew: EntrySelectedInTable.Crew,
				Author: EntrySelectedInTable.Author,
				Content: EntrySelectedInTable.Content,
				CreateTimestamp: convertToServerTime(
					EntrySelectedInTable.CreateTimestamp as Date,
				),
				UpdateTimestamp: EntrySelectedInTable.UpdateTimestamp,
				UpdatedBy: EntrySelectedInTable.UpdatedBy,
			});
			formInitializedForEntryIdRef.current = String(
				EntrySelectedInTable.EntryId,
			);
			setIsLoading(false);
		}
	}, [
		EntrySelectedInTable,
		isNewEntry,
		reset,
		convertToServerTime,
		setIsLoading,
		getValues,
	]);

	useEffect(() => {
		if (isNewEntry) {
			const intervalId = setInterval(() => {
				if (getValues("CreateTimestamp")) {
					setValue("CreateTimestamp", new Date(), {
						shouldDirty: false,
						shouldValidate: false,
					});
				}
			}, 1000);
			return () => clearInterval(intervalId);
		}
	}, [isNewEntry, setValue, getValues]);

	useEffect(() => {
		const fetchCurrentShift = async () => {
			if (!isNewEntry && prevEntryIdRef.current !== EntryId) setIsLoading(true);
			else if (isNewEntry && !initialShiftSetRef.current) setIsLoading(true);
			else if (!isNewEntry) setIsLoading(false);

			setError(null);
			try {
				const res = await getShiftByParameters({
					shiftId: null,
					productionDate: null,
				});
				if (res.ok && res.data) {
					currentProductionShiftRef.current = {
						ShiftId: res.data.CurrentShiftId,
						ProductionDate: res.data.CurrentProductionDate,
						ProductionShift: res.data.CurrentShift,
						Crew: res.data.CurrentCrew,
						Start: res.data.CurrentStart,
						End: res.data.CurrentEnd,
						Shift: res.data.CurrentShift,
					};
					if (isNewEntry && !initialShiftSetRef.current) {
						setValue("ShiftId", res.data.CurrentShiftId, { shouldDirty: true });
						setValue("Shift", res.data.CurrentShift, { shouldDirty: true });
						setValue("Crew", res.data.CurrentCrew, { shouldDirty: true });
						initialShiftSetRef.current = true;
					}
				} else if (!res.ok) {
					const errorRes = res as { message?: string };
					setError(errorRes.message || "Failed to fetch current shift");
				}
			} catch (e: any) {
				setError(e.message || "Error fetching shift data");
			} finally {
				if (isNewEntry || (!isNewEntry && prevEntryIdRef.current !== EntryId)) {
					setIsLoading(false);
				}
			}
		};
		fetchCurrentShift();
	}, [isNewEntry, setValue, setIsLoading, setError, EntryId]);

	if (
		isLoading &&
		formInitializedForEntryIdRef.current !== EntryId &&
		watchedEntryIdFormField !==
			(EntrySelectedInTable?.EntryId || (isNewEntry ? 0 : undefined))
	) {
		return (
			<Paper
				elevation={1}
				className={classes.paper}
				style={{ justifyContent: "center", alignItems: "center" }}
			>
				<LazyLoading />
			</Paper>
		);
	}

	return (
		<>
			{(isNewEntry ||
				(EntrySelectedInTable?.EntryId &&
					watchedEntryIdFormField === EntrySelectedInTable.EntryId)) && (
				<Paper elevation={1} className={classes.paper}>
					<form
						onSubmit={handleSubmit(onSubmit)}
						style={{
							height: "100%",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<DialogTitle
							style={{
								padding: "0px",
								marginLeft: 0,
								width: "100%",
								flexBasis: "10%",
								minHeight: "10%",
							}}
						>
							<Grid item xs={12}>
								<Controller
									name="Title"
									control={control}
									rules={{
										required: "Title is required",
										validate: (value) =>
											!!value?.trim() || "Title cannot be empty",
									}}
									render={({ field }) => (
										<TextField
											label="Title"
											variant="standard"
											fullWidth
											error={!!errors.Title}
											helperText={errors.Title?.message}
											{...field}
											autoFocus={isNewEntry}
											className={classes.titleInput}
											InputLabelProps={{ shrink: true }}
										/>
									)}
								/>
							</Grid>
						</DialogTitle>
						<DialogContent
							dividers
							style={{
								padding: "20px 0 0",
								flexGrow: 1,
								flexBasis: "90%",
								overflowY: "auto",
								display: "flex",
								flexDirection: "column",
								borderTop: 0,
							}}
							className={classes.root}
						>
							<Grid
								container
								spacing={2}
								style={{
									width: "100%",
									marginBottom: "0px",
									paddingLeft: 0,
									display: "flex",
									justifyContent: "space-between",
									margin: 0,
								}}
							>
								<Grid item xs={12} md={4} style={{ paddingLeft: 0 }}>
									<Controller
										name="CreateTimestamp"
										control={control}
										render={({ field: { value, onChange } }) => (
											<DatePicker
												label="Creation Time"
												format="MM/DD/YYYY hh:mm a"
												value={value ? moment(value) : null}
												onChange={(newValue: Moment | null) => {
													if (newValue && newValue.isValid()) {
														onChange(newValue.toDate());
													}
												}}
												disabled={true}
												slotProps={{
													textField: {
														variant: "outlined",
														size: "small",
														className: classes.datePicker,
														disabled: true,
													},
												}}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={12} md={4}>
									<Controller
										name="ShiftId"
										control={control}
										render={({ field }) => (
											<ShiftSelectorAutocomplete
												onShiftSelected={(
													selectedShift: ShiftOption | null,
												) => {
													manuallySelectedShiftRef.current = selectedShift;
													if (selectedShift) {
														setValue("Shift", selectedShift.Shift, {
															shouldDirty: true,
														});
														setValue("Crew", selectedShift.Crew, {
															shouldDirty: true,
														});
														field.onChange(selectedShift.ShiftId);
													} else {
														setValue("Shift", null, { shouldDirty: true });
														setValue("Crew", null, { shouldDirty: true });
														field.onChange(null);
													}
												}}
												inputStyles={{
													height: "40px",
													paddingTop: "0px",
													paddingRight: "0px",
												}}
												classes={
													!disabledField ? classes.inputEditable : undefined
												}
												selectedShiftId={field.value as number | null}
												disabled={disabledField}
											/>
										)}
									/>
								</Grid>
								<Grid
									item
									xs={12}
									md={2}
									style={{ paddingRight: 0, marginRight: "0px" }}
								>
									<Controller
										name="Author"
										control={control}
										rules={{
											required: "Author is required",
										}}
										render={({ field }) => (
											<TextField
												label="Author"
												variant="outlined"
												fullWidth
												error={!!errors.Author}
												helperText={errors.Author?.message}
												InputLabelProps={{
													shrink: true,
												}}
												InputProps={{
													style: { height: "40px" },
												}}
												className={
													isNewEntry
														? classes.inputEditable
														: classes.disabledInput
												}
												autoComplete="off"
												{...field}
												disabled={!isNewEntry}
											/>
										)}
									/>
								</Grid>
								<Grid item md={2} xs={12}>
									<Controller
										name="AssetId"
										control={control}
										rules={{ required: "Asset is required" }}
										render={({ field }) => (
											<AssetsFilter
												title="Asset"
												nullValue=""
												value={field.value as number | null}
												onChange={field.onChange}
												customStyles={{
													border: "2px solid #3F51B5",
												}}
											/>
										)}
									/>
								</Grid>
							</Grid>
							<Grid
								container
								item
								xs={12}
								className={
									!isNewEntry && isEditing ? classes.inputEditable : ""
								}
								style={{
									flexGrow: 1,
									height: "calc(100% - 200px)",
									marginTop: "16px",
								}}
							>
								<Grid item xs={12} style={{ padding: 0, height: "100%" }}>
									<ContentInput
										UpdatedBy={watchedUpdatedBy as string}
										UpdateTimestamp={watchedUpdateTimestamp as Date}
										onChange={(val) => setValue("Content", val)}
										value={watchedContent as string}
										isEditing={isEditing}
										isNewEntry={isNewEntry}
									/>
								</Grid>
							</Grid>
						</DialogContent>
						<Grid
							container
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								paddingLeft: 0,
								marginTop: "10px",
							}}
						>
							<Grid item container md={6} xs={12} spacing={2}>
								<Grid item md={6} xs={12}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={toggleClose}
									>
										Close
									</Button>
								</Grid>
								{!isNewEntry && (
									<Grid item md={6} xs={12}>
										<Button
											fullWidth
											variant="contained"
											color="primary"
											onClick={toggleEdit}
										>
											{isEditing ? "Preview" : "Edit"}
										</Button>
									</Grid>
								)}
							</Grid>
							<Grid
								item
								container
								md={6}
								xs={12}
								style={{ display: "flex", justifyContent: "flex-end" }}
							>
								<Grid item md={6} xs={12}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading && <CircularProgress size="1rem" />
										}
										disabled={
											!isValid ||
											isSubmitLoading ||
											(isLoading && !isSubmitLoading)
										}
										variant="contained"
										color="primary"
										type="submit"
									>
										{isNewEntry ? "Create" : "Save"}
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</form>
				</Paper>
			)}
			<Snackbar
				open={addSuccess}
				autoHideDuration={2500}
				onClose={handleCloseSnackbar}
			>
				<Alert severity="info" onClose={handleCloseSnackbar}>
					{EntryId === "new" || !getValues("EntryId")
						? "Entry created successfully"
						: "Entry updated successfully"}
				</Alert>
			</Snackbar>
			<ConfirmationDialogRaw
				open={showConfirmationDialog}
				onClose={handleConfirmationClose}
				title="Shift Submission Confirmation"
				message={confirmationDialogMessage}
			/>
			<ErrorModal error={error} onHide={() => setError(null)} />
		</>
	);
};
